import { PrimaryButton, TextInput, useAlert } from "@wit/mpesa-ui-components";
import { AlertTypeEnum } from "@wit/mpesa-ui-components/lib/context/alert/alert.context";
import { Formik, FormikHelpers } from "formik";
import i18next from "i18next";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import styled, { ThemeContext } from "styled-components";
import { object, string } from "yup";
import { ConfigContext } from "../../../../../app.component";
import { Row } from "../../../../../shared/shared.styled";
import { getPhoneNumberRegex } from "../../../../../shared/shared.utils";
import RequestMoneyApi from "../../request-money.api";
import { IBlacklistParameters, INewBlockedNumber } from "../../request-money.interfaces";

interface IBlacklistNewNumberProps {
  onAddedNewNumber: () => void;
  requestParams: IBlacklistParameters;
}

/**
 * validation schema for phone number
 */
const getValidationSchema = () => {
  return object().shape({
    phoneNumber: string().required(i18next.t("pages.tools.requestMoney.blacklist.newBlacklistContact.emptyInputError")),
  });
};

/**
 * Blacklist new number component
 */
const BlacklistNewNumber = ({ onAddedNewNumber, requestParams }: IBlacklistNewNumberProps) => {
  const [t] = useTranslation();
  const themeContext = useContext(ThemeContext);
  const [showAlert, hideAlert, setAlertProps] = useAlert();
  const [numberAlreadyExist, setNumberAlreadyExist] = React.useState(false);
  const { config } = React.useContext(ConfigContext);

  /**
   * function to validate phone number
   * @param phoneNumber
   */
  const validatePhoneFormat = (phoneNumber: string) => {
    return phoneNumber.match(getPhoneNumberRegex(config));
  };

  /**
   * function to block new number
   * @param values
   * @param actions
   */
  const addNewBlockedNumber = (values: INewBlockedNumber, actions: FormikHelpers<INewBlockedNumber>) => {
    if (validatePhoneFormat(values.phoneNumber)) {
      actions.setSubmitting(true);
      setNumberAlreadyExist(false);
      RequestMoneyApi.methods
        .addToBlacklist(values, requestParams)
        .finally(() => actions.setSubmitting(false))
        .then(
          res => {
            onAddedNewNumber();
            actions.resetForm();
          },
          e => {
            if (
              e &&
              e.data &&
              e.data.status &&
              e.data.status.message &&
              e.data.status.message ===
                t("pages.tools.requestMoney.blacklist.newBlacklistContact.alreadyExistsNumberError")
            ) {
              setNumberAlreadyExist(true);
            } else {
              setAlertProps({
                title: t("pages.tools.requestMoney.blacklist.newBlacklistContact.newNumberError"),
                type: AlertTypeEnum.ERROR,
              });
              showAlert();
            }
          },
        );
    } else {
      actions.setSubmitting(false);
      actions.setFieldError(
        "phoneNumber",
        t("pages.tools.requestMoney.blacklist.newBlacklistContact.validNumberError"),
      );
    }
  };

  return (
    <FormContainer>
      <FormContainerTitle>{t("pages.tools.requestMoney.blacklist.newBlacklistContact.title")}</FormContainerTitle>
      <Formik
        onSubmit={addNewBlockedNumber}
        initialValues={{ phoneNumber: "" }}
        validationSchema={getValidationSchema()}
        render={({ handleChange, values, handleSubmit, errors, isSubmitting, resetForm }) => (
          <form onSubmit={handleSubmit}>
            <Row style={{ alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
              <div style={{ flex: "1 0 25%", minWidth: "264px" }}>
                <FieldSection>
                  <TextInput
                    title={t("pages.tools.requestMoney.blacklist.newBlacklistContact.phoneNumber")}
                    placeholder={t("pages.tools.requestMoney.blacklist.newBlacklistContact.insertPhonePlaceholder")}
                    name="phoneNumber"
                    onChange={handleChange}
                    value={values.phoneNumber}
                    error={errors.phoneNumber}
                    autoComplete="off"
                    maxLength={14}
                    required
                  />
                </FieldSection>
              </div>
              <div style={{ flex: "1 1 50%", display: "flex", justifyContent: "flex-end" }}>
                <ButtonContainer>
                  <PrimaryButton
                    titleLabel={t("pages.tools.requestMoney.blacklist.newBlacklistContact.addToBlacklist")}
                    type="submit"
                    redTheme={true}
                    loading={isSubmitting}
                    safaricom={themeContext.palette.vodafoneRed === "#2fc56d"}
                    id="add-blacklist-button"
                  />
                </ButtonContainer>
              </div>
            </Row>
            {numberAlreadyExist ? (
              <ErrorRow>
                <span style={{ fontSize: "16px;", fontWeight: "bold" }}>
                  {t("pages.tools.requestMoney.blacklist.newBlacklistContact.alreadyExistsNumber.title")}
                </span>
                <span style={{ fontSize: "14px;" }}>
                  {t("pages.tools.requestMoney.blacklist.newBlacklistContact.alreadyExistsNumber.description")}
                </span>
              </ErrorRow>
            ) : null}
          </form>
        )}
      />
    </FormContainer>
  );
};

const FormContainer = styled("div")`
  display: flex;
  flex-direction: column;
  padding: 31px 47px;
  margin: 0px -47px 0px -47px;
  background-color: ${props => props.theme.palette.greyLight};
`;

const FormContainerTitle = styled("span")`
  font-family: Vodafone Rg;
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 0.4px;
  color: ${props => props.theme.palette.midGrey};
  text-transform: uppercase;
  margin-bottom: 21px;
`;

const FieldSection = styled("div")``;

const ButtonContainer = styled("div")`
  width: fit-content;
`;

const ErrorRow = styled(Row)`
  font-family: Vodafone Rg;
  color: red;
  margin-top: 12px;
  margin-bottom: 18px;
  flex-direction: column;
  align-items: flex-start;
`;

export default BlacklistNewNumber;
